import { useState, type ReactElement } from 'react';
import { Menu, MenuItem as MuiMenuItem } from '@mui/material';
import { GripVertical, ChevronUp, Plus, Trash2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ItemErrorText } from './ItemErrorText';
import { RowInput } from './RowInput';
import { SortableItem } from './SortableItem';
import { SortableGroupLink } from './SortableGroupLink';
import { FeaturePickerModal } from './FeaturePickerModal';
import { PagePickerModal } from './PagePickerModal';
import { LABEL_ADD_EXTERNAL_URL, LABEL_ADD_HEALTHERA_FEATURE, LABEL_ADD_PAGE } from './labels';
import { withScheme } from './menuUrl';

export type LinkRow = { label: string; url: string; isReadOnly?: boolean };
export type LinkItem = { kind: 'link'; label: string; url: string; isReadOnly?: boolean };
export type GroupItem = { kind: 'group'; label: string; links: LinkRow[] };
export type MenuItem = LinkItem | GroupItem;

export interface LinkRowErrors {
  label?: string;
  url?: string;
}

export interface MenuItemErrors extends LinkRowErrors {
  item?: string;
  links?: Record<number, LinkRowErrors>;
}

export interface MenuBuilderCoreProps {
  items: MenuItem[];
  onChange: (items: MenuItem[]) => void;
  ro?: boolean;
  errors?: string[];
  itemErrors?: Record<number, MenuItemErrors>;
}

type FeatureModalTarget = 'top' | number;

function reorderCollapsed(set: Set<number>, oldIdx: number, newIdx: number): Set<number> {
  const next = new Set<number>();
  set.forEach((i) => {
    if (i === oldIdx) {
      next.add(newIdx);
    } else if (oldIdx < newIdx && i > oldIdx && i <= newIdx) {
      next.add(i - 1);
    } else if (oldIdx > newIdx && i >= newIdx && i < oldIdx) {
      next.add(i + 1);
    } else {
      next.add(i);
    }
  });
  return next;
}

function renderLinkFields(
  it: LinkItem,
  i: number,
  itemError: MenuItemErrors | undefined,
  ro: boolean | undefined,
  patchItem: (index: number, patch: Record<string, unknown>) => void
): ReactElement {
  if (it.isReadOnly) {
    return (
      <span className="vtwo:flex-1 vtwo:text-sm vtwo:text-gray-700 vtwo:truncate">{it.label}</span>
    );
  }
  return (
    <>
      <RowInput
        value={it.label}
        onChange={(v) => patchItem(i, { label: v })}
        placeholder="Menu title"
        disabled={ro}
        error={Boolean(itemError?.label)}
      />
      <RowInput
        value={it.url}
        onChange={(v) => patchItem(i, { url: v })}
        complete={withScheme}
        placeholder="URL"
        disabled={ro}
        error={Boolean(itemError?.url)}
      />
    </>
  );
}

export const MenuBuilderCore = ({
  items,
  onChange,
  ro,
  errors = [],
  itemErrors = {},
}: MenuBuilderCoreProps): ReactElement => {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  const [emptyGroupMenu, setEmptyGroupMenu] = useState<{
    anchor: HTMLElement;
    index: number;
  } | null>(null);
  const [featureModalTarget, setFeatureModalTarget] = useState<FeatureModalTarget | null>(null);
  const [pageModalTarget, setPageModalTarget] = useState<FeatureModalTarget | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const update = (next: MenuItem[]) => onChange(next);

  const patchItem = (i: number, patch: Record<string, unknown>) =>
    update(items.map((it, idx) => (idx === i ? ({ ...it, ...patch } as MenuItem) : it)));

  const removeItem = (i: number) => {
    setCollapsed((prev) => {
      const next = new Set<number>();
      prev.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
    update(items.filter((_, idx) => idx !== i));
  };

  const addLink = () => update([...items, { kind: 'link', label: '', url: '', isReadOnly: false }]);
  const addGroup = () => update([...items, { kind: 'group', label: '', links: [] }]);

  const addFeatureLink = (item: { title: string; href: string }) =>
    update([
      ...items,
      { kind: 'link' as const, label: item.title, url: item.href, isReadOnly: true },
    ]);

  const addGroupLink = (gi: number) =>
    update(
      items.map((it, idx) =>
        idx === gi && it.kind === 'group'
          ? { ...it, links: [...it.links, { label: '', url: '', isReadOnly: false }] }
          : it
      )
    );

  const addFeatureGroupLink = (gi: number, item: { title: string; href: string }) =>
    update(
      items.map((it, idx) =>
        idx === gi && it.kind === 'group'
          ? { ...it, links: [...it.links, { label: item.title, url: item.href, isReadOnly: true }] }
          : it
      )
    );

  const handleFeatureSelect = (item: { title: string; href: string }) => {
    if (featureModalTarget === 'top') addFeatureLink(item);
    else if (typeof featureModalTarget === 'number') addFeatureGroupLink(featureModalTarget, item);
  };

  const handlePageSelect = (item: { title: string; href: string }) => {
    if (pageModalTarget === 'top') addFeatureLink(item);
    else if (typeof pageModalTarget === 'number') addFeatureGroupLink(pageModalTarget, item);
  };

  const setGroupLink = (gi: number, li: number, patch: Partial<LinkRow>) =>
    update(
      items.map((it, idx) => {
        if (idx !== gi || it.kind !== 'group') return it;
        return { ...it, links: it.links.map((l, k) => (k === li ? { ...l, ...patch } : l)) };
      })
    );

  const removeGroupLink = (gi: number, li: number) =>
    update(
      items.map((it, idx) =>
        idx === gi && it.kind === 'group'
          ? { ...it, links: it.links.filter((_, k) => k !== li) }
          : it
      )
    );

  const toggleCollapsed = (i: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);

      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }

      return next;
    });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((_, i) => `item-${i}` === String(active.id));
    const newIndex = items.findIndex((_, i) => `item-${i}` === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    setCollapsed((prev) => reorderCollapsed(prev, oldIndex, newIndex));
    update(arrayMove(items, oldIndex, newIndex));
  };

  const handleGroupLinkDragEnd = (gi: number) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const group = items[gi];
    if (group?.kind !== 'group') return;
    const oldIndex = group.links.findIndex((_, li) => `glink-${gi}-${li}` === String(active.id));
    const newIndex = group.links.findIndex((_, li) => `glink-${gi}-${li}` === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    update(
      items.map((it, idx) => {
        if (idx !== gi || it.kind !== 'group') return it;
        return { ...it, links: arrayMove(it.links, oldIndex, newIndex) };
      })
    );
  };

  return (
    <div className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={items.map((_, i) => `item-${i}`)}
          strategy={verticalListSortingStrategy}
        >
          {items.map((it, i) => {
            const itemError = itemErrors[i];

            if (it.kind === 'link') {
              const editableFields = renderLinkFields(it, i, itemError, ro, patchItem);

              return (
                <SortableItem key={`item-${i}`} id={`item-${i}`} disabled={ro}>
                  {(dragHandleProps) => (
                    <div className="vtwo:border vtwo:border-gray-200 vtwo:rounded-lg vtwo:px-2 vtwo:py-1.5">
                      <div className="vtwo:flex vtwo:items-center vtwo:gap-2">
                        <div
                          {...dragHandleProps}
                          className="vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
                        >
                          <GripVertical className="vtwo:h-4 vtwo:w-4 vtwo:text-gray-400" />
                        </div>
                        {editableFields}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="vtwo:h-7 vtwo:w-7 vtwo:shrink-0"
                          onClick={() => removeItem(i)}
                          disabled={ro}
                        >
                          <Trash2 className="vtwo:h-3 vtwo:w-3 vtwo:text-gray-400" />
                        </Button>
                      </div>
                      <ItemErrorText
                        messages={[itemError?.label, itemError?.url, itemError?.item]}
                      />
                    </div>
                  )}
                </SortableItem>
              );
            }

            const isCollapsed = collapsed.has(i);

            return (
              <SortableItem key={`item-${i}`} id={`item-${i}`} disabled={ro}>
                {(dragHandleProps) => (
                  <div className="vtwo:border vtwo:border-dashed vtwo:border-blue-400 vtwo:rounded-lg vtwo:p-2">
                    <div className="vtwo:flex vtwo:items-center vtwo:gap-2">
                      <div
                        {...dragHandleProps}
                        className="vtwo:cursor-grab vtwo:shrink-0 vtwo:touch-none"
                      >
                        <GripVertical className="vtwo:h-4 vtwo:w-4 vtwo:text-gray-400" />
                      </div>
                      <RowInput
                        value={it.label}
                        onChange={(v) => patchItem(i, { label: v })}
                        placeholder="Group title"
                        disabled={ro}
                        error={Boolean(itemError?.label)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="vtwo:h-7 vtwo:w-7 vtwo:shrink-0"
                        onClick={() => removeItem(i)}
                        disabled={ro}
                      >
                        <Trash2 className="vtwo:h-3 vtwo:w-3 vtwo:text-gray-400" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="vtwo:h-7 vtwo:w-7 vtwo:shrink-0"
                        onClick={() => toggleCollapsed(i)}
                      >
                        <ChevronUp
                          className={cn(
                            'vtwo:h-4 vtwo:w-4 vtwo:transition-transform',
                            isCollapsed && 'vtwo:rotate-180'
                          )}
                        />
                      </Button>
                    </div>
                    <ItemErrorText messages={[itemError?.label, itemError?.item]} />

                    {!isCollapsed && (
                      <div className="vtwo:mt-2 vtwo:pl-1">
                        <p className="vtwo:text-xs vtwo:font-medium vtwo:text-gray-600 vtwo:mb-1">
                          Sub menu Items:
                        </p>
                        <div className="vtwo:flex vtwo:flex-col vtwo:gap-1.5">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleGroupLinkDragEnd(i)}
                            modifiers={[restrictToVerticalAxis]}
                          >
                            <SortableContext
                              items={it.links.map((_, li) => `glink-${i}-${li}`)}
                              strategy={verticalListSortingStrategy}
                            >
                              {it.links.map((l, li) => (
                                <SortableGroupLink
                                  key={`glink-${i}-${li}`}
                                  id={`glink-${i}-${li}`}
                                  link={l}
                                  ro={ro}
                                  errors={itemError?.links?.[li]}
                                  onLabelChange={(v) => setGroupLink(i, li, { label: v })}
                                  onUrlChange={(v) => setGroupLink(i, li, { url: v })}
                                  onRemove={() => removeGroupLink(i, li)}
                                  onAddExternal={() => addGroupLink(i)}
                                  onAddFeature={() => setFeatureModalTarget(i)}
                                  onAddPage={() => setPageModalTarget(i)}
                                />
                              ))}
                            </SortableContext>
                          </DndContext>

                          {it.links.length === 0 && (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="vtwo:self-start vtwo:text-xs vtwo:h-7"
                                onClick={(e) =>
                                  setEmptyGroupMenu({ anchor: e.currentTarget, index: i })
                                }
                                disabled={ro}
                              >
                                <Plus className="vtwo:h-3 vtwo:w-3" />
                                Add link
                              </Button>
                              <Menu
                                anchorEl={emptyGroupMenu?.anchor ?? null}
                                open={emptyGroupMenu?.index === i}
                                onClose={() => setEmptyGroupMenu(null)}
                              >
                                <MuiMenuItem
                                  onClick={() => {
                                    addGroupLink(i);
                                    setEmptyGroupMenu(null);
                                  }}
                                >
                                  {LABEL_ADD_EXTERNAL_URL}
                                </MuiMenuItem>
                                <MuiMenuItem
                                  onClick={() => {
                                    setEmptyGroupMenu(null);
                                    setFeatureModalTarget(i);
                                  }}
                                >
                                  {LABEL_ADD_HEALTHERA_FEATURE}
                                </MuiMenuItem>
                                <MuiMenuItem
                                  onClick={() => {
                                    setEmptyGroupMenu(null);
                                    setPageModalTarget(i);
                                  }}
                                >
                                  {LABEL_ADD_PAGE}
                                </MuiMenuItem>
                              </Menu>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SortableItem>
            );
          })}
        </SortableContext>
      </DndContext>

      <div className="vtwo:flex vtwo:gap-2 vtwo:mt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={ro}
          onClick={(e) => setAddMenuAnchor(e.currentTarget)}
        >
          Add link
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
        >
          <MuiMenuItem
            onClick={() => {
              addLink();
              setAddMenuAnchor(null);
            }}
          >
            {LABEL_ADD_EXTERNAL_URL}
          </MuiMenuItem>
          <MuiMenuItem
            onClick={() => {
              setAddMenuAnchor(null);
              setFeatureModalTarget('top');
            }}
          >
            {LABEL_ADD_HEALTHERA_FEATURE}
          </MuiMenuItem>
          <MuiMenuItem
            onClick={() => {
              setAddMenuAnchor(null);
              setPageModalTarget('top');
            }}
          >
            {LABEL_ADD_PAGE}
          </MuiMenuItem>
        </Menu>
        <Button type="button" variant="outline" size="sm" onClick={addGroup} disabled={ro}>
          Add link group
        </Button>
      </div>

      <FeaturePickerModal
        open={featureModalTarget !== null}
        onClose={() => setFeatureModalTarget(null)}
        onSelect={handleFeatureSelect}
      />

      {pageModalTarget !== null && (
        <PagePickerModal
          open
          onClose={() => setPageModalTarget(null)}
          onSelect={handlePageSelect}
        />
      )}

      {errors.length > 0 && (
        <ul className="vtwo:list-none vtwo:p-0 vtwo:m-0">
          {errors.map((m, k) => (
            <li key={k} className="vtwo:text-xs vtwo:text-red-500">
              {m}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
